import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import EmbeddedClinicList from "../../components/EmbeddedClinicList";
import {
  TwoColRow,
  ColText,
  ColImage,
  ColImg,
  ThreeColRow,
  StepCard,
  StepCardIcon,
  StepCardTitle,
  StepCardDesc,
} from "../../components/ContentLayouts";

// ── Image imports ─────────────────────────────────────────────────────────────
import imgVetServices from "../../assets/images/content/online-vet-consultation/vet-services.jpg";
import iconHowToBook1 from "../../assets/images/icons/location.png";
import iconHowToBook2 from "../../assets/images/icons/review-list.png";
import iconHowToBook3 from "../../assets/images/icons/med-calendar.png";
import imgNutrition from "../../assets/images/content/online-vet-consultation/nutrition.jpg";

const faqs = [
  {
    question: "What does PetQura's veterinary consulting service include?",
    answer:
      "Our veterinary consulting services include location-based clinic appointment bookings within Bangalore with trusted and expert vets.",
  },
  {
    question: "How is a veterinary consultant different from a regular vet?",
    answer:
      "An in-person veterinary consultant provides targeted advice, specialized dietary insights, and preliminary assessments to help you choose the best care path as opposed to veterinary doctor online consultation.",
  },
  {
    question: "Can I get pet nutrition consulting through PetQura?",
    answer:
      "Yes, pet consultancy provides tailored dietary plans, weight management support, and breed-specific feeding recommendations from certified experts.",
  },
  {
    question: "When should I book a vet consult?",
    answer:
      "You can book an online vet consult for symptoms, follow-up checkups, dietary questions, or general wellness advice for your pet.",
  },
  {
    question: "How do I book a veterinary doctor consultation on PetQura?",
    answer:
      "Simply select your required service on our platform, choose a verified doctor based on location or specialty, pick a convenient time slot, and complete the secure booking.",
  },
  {
    question: "How long does a vet consult session last?",
    answer:
      "A standard vet consultation typically lasts between 15 to 30 minutes, giving the doctor ample time to review symptoms and answer all your questions thoroughly.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const OnlineVetConsultation = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Book Vet Consultation in Bangalore | PetQura</title>
        <meta
          name="description"
          content="Find certified veterinary consultants in Bangalore through PetQura. Book pet consultation, pet nutrition consulting, or veterinary doctor consultation for dogs, cats & all pets in three simple steps."
        />
        <link
          rel="canonical"
          href="https://petqura.com/services/vet-consultation-bangalore"
        />
      </Helmet>

      <PageWrapper>
        <HeroSection>
          <h1>PetQura: Your Trusted Veterinary Consultant in Bangalore</h1>
        </HeroSection>

        <EmbeddedClinicList serviceId={null} previewMode />

        {/* ── Intro paragraph ── */}
        <ContentSection>
          <p>
            Caring for your pet is a journey filled with love, learning, and
            occasional worries. When your furry family member feels unwell or
            needs expert health guidance, navigating options across Bangalore
            should not add to your stress. That is why we built a platform
            designed to help you locate the nearest veterinary clinic. Getting a
            reliable vet consultation is now straightforward and comfortable. We
            connect pet parents directly with experienced professionals, helping
            you get trusted advice right when you need it most. Book your pet
            consultation through PetQura today and give your pet the calm,
            loving care they deserve!
          </p>
        </ContentSection>

        {/* ── What Are Vet Consulting Services — 2-col: text left / image right ── */}
        <ContentSection>
          <SectionHeading>
            What Are PetQura's Veterinary Consulting Services?
          </SectionHeading>
          <TwoColRow>
            <ColText>
              <p>
                We offer comprehensive veterinary consulting services tailored
                to suit your lifestyle and your pet's specific needs. Our goal
                is to reduce travel anxiety for pets by connecting you with
                top-rated clinics nearby. While we don't provide online
                veterinary consultation, our pet consultancy platform allows you
                to choose the date and time of your convenience to schedule
                clinic visits.
              </p>
              <p>
                By bringing accessibility and convenience together, our vet
                consultation ensures that high-quality healthcare remains
                stress-free and reliable for every household.
              </p>
            </ColText>
            <ColImage>
              <ColImg
                src={imgVetServices}
                alt="PetQura veterinary consulting services"
              />
            </ColImage>
          </TwoColRow>
        </ContentSection>

        {/* ── How to Book — 3-row: intro / 3-col steps / closing ── */}
        <ContentSection>
          <SectionHeading>
            How to Book Your Veterinary Consulting Service
          </SectionHeading>
          <p>
            Scheduling a pet consultation on PetQura is fast, seamless, and
            user-friendly. We have streamlined our booking process into three
            simple steps:
          </p>
          <ThreeColRow>
            <StepCard>
              <StepCardIcon src={iconHowToBook1} alt="Enter location" />
              <StepCardTitle>Enter Location &amp; Service</StepCardTitle>
              <StepCardDesc>
                Enter desired location and select the specific service your pet
                needs.
              </StepCardDesc>
            </StepCard>
            <StepCard>
              <StepCardIcon src={iconHowToBook2} alt="Review clinics" />
              <StepCardTitle>Review &amp; Choose Clinic</StepCardTitle>
              <StepCardDesc>
                Review list of nearby veterinary experts and choose the clinic
                that best fits your requirements.
              </StepCardDesc>
            </StepCard>
            <StepCard>
              <StepCardIcon src={iconHowToBook3} alt="Confirm appointment" />
              <StepCardTitle>Select Time &amp; Confirm</StepCardTitle>
              <StepCardDesc>
                Select your time slot and confirm the appointment with just a
                few clicks.
              </StepCardDesc>
            </StepCard>
          </ThreeColRow>
          <p>
            Veterinary consulting services guarantee fast response times,
            transparent pricing, and effortless access to quality healthcare.
          </p>
        </ContentSection>

        {/* ── Talk to a Vet ── */}
        <ContentSection>
          <SectionHeading>
            Talk to a Veterinary Doctor for Expert Consultation
          </SectionHeading>
          <p>
            Every pet deserves specialized attention from qualified medical
            professionals. When you book a veterinary doctor consultation
            through PetQura, you are connected with certified, verified
            veterinarians across Bangalore who possess extensive experience in
            clinical care and animal wellness.
          </p>
          <p>
            Our smart matching system helps you find doctors based on your
            specific requirements, location, and preferred appointment timing.
            Each veterinary consultant on our network is dedicated to providing
            compassionate, thorough care. Whether you are dealing with seasonal
            allergies, minor behavioral changes, or digestive troubles, talking
            to a pet consultant in person gives you clear direction and complete
            peace of mind.
          </p>
        </ContentSection>

        {/* ── Pet Nutrition Consulting — 2-col: text left / image right ── */}
        <ContentSection>
          <SectionHeading>
            Pet Nutrition Consulting - Feed Your Pet Right
          </SectionHeading>
          <TwoColRow>
            <ColText>
              <p>
                Proper nutrition is the foundation of a long, energetic, and
                happy life for your pet. Every breed, age group, and health
                condition demands a carefully balanced diet tailored to
                individual needs. Through specialized pet nutrition consulting,
                pet parents can move away from guesswork and establish
                wholesome eating habits.
              </p>
              <p>
                Whether you have a growing puppy needing specific nutrients, a
                senior dog requiring joint support, or a pet needing weight
                management, pet nutrition consulting provides clear, practical
                feeding guidelines. Schedule a diet assessment today to help
                your pet thrive from the inside out!
              </p>
            </ColText>
            <ColImage>
              <ColImg src={imgNutrition} alt="Pet nutrition consulting" />
            </ColImage>
          </TwoColRow>
        </ContentSection>

        {/* ── Who Should Book ── */}
        <ContentSection>
          <SectionHeading>Who Should Book a Pet Consultation?</SectionHeading>
          <p>
            Not every situation requires an immediate emergency rush to the
            clinic. Booking a pet consultancy through PetQura is ideal for
            various everyday wellness scenarios:
          </p>
          <BulletList>
            <li>
              <strong>New Pet Parents:</strong> Get guidance on vaccination
              schedules, growth milestones, basic training, and diet routines
              from a trusted pet consultant.
            </li>
            <li>
              <strong>Mild Symptoms:</strong> Address non-emergency issues like
              mild itching, minor digestive upset, or skin irritation quickly
              through a vet consult.
            </li>
            <li>
              <strong>Follow-up Check-ins:</strong> Easily review recovery
              progress, lab reports, or post-treatment care without causing
              extra travel stress.
            </li>
            <li>
              <strong>Diet &amp; Lifestyle Queries:</strong> Access tailored
              nutrition advice to manage weight or food sensitivities.
            </li>
            <li>
              <strong>Second Opinions:</strong> Get extra clarity on ongoing
              treatments before making major care decisions.
            </li>
          </BulletList>
        </ContentSection>

        {/* ── FAQ ── */}
        <ContentSection>
          <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default OnlineVetConsultation;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5% 5rem;

  @media (max-width: 768px) {
    padding: 2rem 5% 4rem;
  }
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 3rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.5rem;
    font-family: Montserrat, sans-serif;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
